import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  CheckCircleIcon,
  EllipsisHorizontalCircleIcon,
  QuestionMarkCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import LearnersIcon from '../../icons/LearnersIcon';
import { DURATION_NORMAL, EASE_OUT } from '@/lib/motion';
import type {
  AttendeeResponseStatus,
  CalendarEventAttendee,
  Person,
} from '../../../types/integrations';

interface EventPeopleProps {
  organizer?: Person | null;
  creator?: Person | null;
  attendees?: CalendarEventAttendee[];
}

const RSVP_META: Record<
  AttendeeResponseStatus | 'unknown',
  { Icon: React.ComponentType<{ className?: string }>; tone: string; label: string }
> = {
  accepted: { Icon: CheckCircleIcon, tone: 'text-success-500', label: 'Accepted' },
  declined: { Icon: XCircleIcon, tone: 'text-error-500', label: 'Declined' },
  tentative: { Icon: QuestionMarkCircleIcon, tone: 'text-warning-500', label: 'Maybe' },
  needsAction: {
    Icon: EllipsisHorizontalCircleIcon,
    tone: 'text-text-tertiary',
    label: 'No response',
  },
  unknown: {
    Icon: EllipsisHorizontalCircleIcon,
    tone: 'text-text-tertiary',
    label: 'No response',
  },
};

const initials = (name: string | null | undefined, email: string): string => {
  const source = name?.trim() || email;
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
};

const personSameAs = (a?: Person | null, b?: Person | null): boolean =>
  !!a && !!b && a.email.toLowerCase() === b.email.toLowerCase();

const STAGGER_LIMIT = 8;

const EventPeople: React.FC<EventPeopleProps> = ({
  organizer,
  creator,
  attendees,
}) => {
  const [expanded, setExpanded] = useState(false);
  const list = attendees ?? [];
  if (!organizer && !creator && list.length === 0) return null;

  const showCreator = creator && !personSameAs(organizer, creator);
  const VISIBLE_COLLAPSED = 6;
  const visibleList = expanded ? list : list.slice(0, VISIBLE_COLLAPSED);
  const hiddenCount = list.length - visibleList.length;

  return (
    <section className="space-y-2">
      <header className="flex items-center gap-2">
        <LearnersIcon className="w-4 h-4 text-text-tertiary" />
        <h3 className="text-xs font-bold uppercase tracking-wider text-text-secondary">
          {list.length > 0 ? `Attendees · ${list.length}` : 'People'}
        </h3>
      </header>

      {(organizer || showCreator) && (
        <div className="flex flex-wrap gap-1.5 pl-6">
          {organizer && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-surface-elevated text-xs text-text-secondary">
              <span className="font-semibold text-text-primary">
                {organizer.display_name || organizer.email}
              </span>
              <span className="text-text-tertiary">· Organizer</span>
            </span>
          )}
          {showCreator && creator && (
            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-surface-elevated text-xs text-text-secondary">
              <span className="font-semibold text-text-primary">
                {creator.display_name || creator.email}
              </span>
              <span className="text-text-tertiary">· Created</span>
            </span>
          )}
        </div>
      )}

      {visibleList.length > 0 && (
        <ul className="pl-6 space-y-1.5">
          {visibleList.map((a, idx) => {
            const key = (a.response_status as AttendeeResponseStatus) || 'unknown';
            const meta = RSVP_META[key] ?? RSVP_META.unknown;
            const delay = idx < STAGGER_LIMIT ? idx * 0.03 : 0;
            return (
              <motion.li
                key={`${a.email}-${idx}`}
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: DURATION_NORMAL, ease: EASE_OUT, delay }}
                className="flex items-center gap-2.5"
              >
                <span
                  className="w-7 h-7 rounded-full bg-primary-500/12 text-primary-600 dark:text-primary-200 text-[11px] font-bold flex items-center justify-center shrink-0"
                  aria-hidden
                >
                  {initials(a.display_name, a.email)}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="text-sm font-medium text-text-primary truncate">
                      {a.display_name || a.email}
                    </span>
                    {a.self && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary-500 px-1.5 py-0.5 bg-primary-500/12 rounded-full">
                        You
                      </span>
                    )}
                    {a.organizer && !a.self && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-text-secondary px-1.5 py-0.5 bg-surface-elevated rounded-full">
                        Organizer
                      </span>
                    )}
                    {a.optional && (
                      <span className="text-[10px] font-medium text-text-tertiary">
                        optional
                      </span>
                    )}
                  </div>
                  {a.display_name && (
                    <p className="text-xs text-text-tertiary truncate">{a.email}</p>
                  )}
                </div>
                <span
                  className={['inline-flex items-center gap-1 text-xs', meta.tone].join(' ')}
                  title={meta.label}
                >
                  <meta.Icon className="w-4 h-4" />
                </span>
              </motion.li>
            );
          })}
        </ul>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="ml-6 text-xs font-medium text-primary-500 hover:underline"
        >
          Show {hiddenCount} more
        </button>
      )}
    </section>
  );
};

export default EventPeople;
